import { ThemeProvider } from '@backend/lib-design-system';
import '@fontsource/red-hat-display/400.css';
import '@fontsource/red-hat-display/500.css';
import '@fontsource/red-hat-display/700.css';
import App, { type AppContext, type AppProps } from 'next/app';
import Head from 'next/head';
import { createGlobalStyle } from 'styled-components';

import ConsentManager from '~/components/Analytics/ConsentManager';
import SegmentSnippet from '~/components/Analytics/SegmentSnippet';
import { JsonLd } from '~/components/JsonLd/jsonLd';
import { Layout } from '~/components/Layout/Layout';
import { getJobsBusinessUnitUrl } from '~/config';
import {
  type BusinessUnitContextConfig,
  BusinessUnitContextProvider,
} from '~/contexts/BusinessUnitContext/BusinessUnitContext';
import { TrackingContextProvider } from '~/contexts/TrackingContext';
import { TranslationsContextProvider } from '~/contexts/TranslationsContext/TranslationsContext';
import { useCanonicalUrl, useParentViewportListener } from '~/hooks';
import { promanTheme, theme } from '~/themes/default';
import type { AppProps as SharedAppProps } from '~/utils/appProps';
import { extractBusinessUnitFromContext, getDefaultBusinessUnit } from '~/utils/businessUnit';
import { isProduction, loadPublicEnv } from '~/utils/env';
import type { HubPagination } from '~/utils/hubPage';
import { initSentry } from '~/utils/sentry';

/** to override some user-agent style, we should think of including a styled component equivalent to reset.css */
const GlobalStyle = createGlobalStyle`
  html,
  body {
    margin: 0;
    padding: 0;
  }

  *, *::before, *::after {
    box-sizing: border-box;
  }

  .grecaptcha-badge {
    visibility: hidden;
  }
`;

type MyAppProps = AppProps<{ jsonld: Record<string, any>; pagination?: HubPagination }> &
  SharedAppProps & {
    err: any;
    businessUnitConfig: BusinessUnitContextConfig;
  };

let isSentryInit = false;

export default function MyApp({
  Component,
  pageProps,
  err,
  env,
  translations,
  jobList,
  sectorList,
  cityList,
  businessUnitConfig,
}: MyAppProps) {
  loadPublicEnv(env); // this must be placed at the top to avoid missing env variables

  if (!isSentryInit) {
    isSentryInit = true;
    initSentry();
  }

  const { brandName, businessUnit, country, locale, iframe, isProman } = businessUnitConfig;

  const canonicalUrl = useCanonicalUrl(businessUnit, pageProps.pagination?.currentPage);
  useParentViewportListener(iframe);

  return (
    <BusinessUnitContextProvider businessUnitConfig={businessUnitConfig}>
      <Head>
        <meta key="viewport" name="viewport" content="width=device-width, initial-scale=1" />
        {!isProduction() && <meta name="robots" content="noindex" />}
        <meta name="referrer" content="same-origin" />
        {!isProman && (
          <>
            <meta name="twitter:card" content="summary" />
            <meta name="twitter:site" content={`@iziwork${country}`} />
            <meta name="twitter:creator" content={`@iziwork${country}`} />
          </>
        )}
        <meta property="og:site_name" content={isProman ? brandName : 'iziwork Jobs'} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:site" content={getJobsBusinessUnitUrl(businessUnit)} />
        <meta property="og:type" content="website" />
        {/* Open Graph spells locales language_TERRITORY */}
        <meta property="og:locale" content={locale.replace('-', '_')} />
        {isProman ? (
          <link rel="icon" href="/static/favicon-proman.ico" />
        ) : (
          <>
            <link rel="icon" href="/images/favicon-iziwork.svg" type="image/svg+xml" />
            <link rel="icon" href="/images/favicon.ico" />
          </>
        )}
        <link rel="canonical" href={canonicalUrl} />
      </Head>
      {!iframe && <SegmentSnippet />}
      <JsonLd extraJsonLd={pageProps.jsonld} />
      <ThemeProvider theme={isProman ? promanTheme : theme}>
        <TranslationsContextProvider translations={translations}>
          <TrackingContextProvider>
            <GlobalStyle />
            <Layout jobList={jobList} cityList={cityList} sectorList={sectorList}>
              <Component {...pageProps} err={err} />
            </Layout>
            {!iframe && <ConsentManager />}
          </TrackingContextProvider>
        </TranslationsContextProvider>
      </ThemeProvider>
    </BusinessUnitContextProvider>
  );
}

MyApp.getInitialProps = async (context: AppContext) => {
  const ctx = await App.getInitialProps(context);

  // The browser only calls this to render a 404 or an error page after a client navigation (a page with
  // getServerSideProps gets these props from the server along with its data): it keeps those of the hydrated page.
  if (typeof window !== 'undefined') {
    // set by the server below: __NEXT_DATA__ is typed for any app
    const { pageProps, ...appProps } = window.__NEXT_DATA__.props as SharedAppProps & { pageProps: unknown };
    return { ...appProps, ...ctx };
  }

  const { getAppProps } = await import('~/utils/appProps');
  const businessUnit = extractBusinessUnitFromContext(context.ctx) || getDefaultBusinessUnit();

  return { ...ctx, ...getAppProps(businessUnit) };
};
